import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, MapPin, Plus, Check, Search, Target, Info, DollarSign, Tag, Link2, Sliders, Shield,
} from "lucide-react";
import { sr } from "../../../lib/sr";
import { Head, Honest } from "./shared";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export function Locations() {
  const [locs, setLocs] = useState(null);
  const [offers, setOffers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [adding, setAdding] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [twin, setTwin] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = () => Promise.all([
    sr.locations().then((r) => setLocs(r.locations)).catch(() => setLocs([])),
    sr.locationsBudgetSummary().then(setSummary).catch(() => setSummary(null)),
  ]);
  useEffect(() => {
    load();
    sr.offerStudio.list().then((r) => setOffers(r.offers || [])).catch(() => setOffers([]));
  }, []);

  const search = async () => {
    if (!q.trim()) return;
    setBusy(true);
    try { const r = await sr.importBusiness(q); setResults(r.results || []); if (!r.configured) toast.message("Place lookup pending external activation"); }
    catch { toast.error("Search failed"); } finally { setBusy(false); }
  };
  const add = async (p) => {
    try {
      const r = await sr.createLocation({ name: p.name, address: p.address, lat: p.lat, lng: p.lng, place_id: p.place_id, category: p.category });
      if (r.location?.place_id) await sr.verifyLocation(r.location.id);
      toast.success("Location added"); setAdding(false); setResults([]); setQ(""); load();
    } catch { toast.error("Could not add"); }
  };
  const discover = async (loc) => {
    setBusy(true); setTwin(null);
    try {
      const r = await sr.discoverCompetitors(loc.id, 5000);
      if (!r.configured) { toast.message("Competitor discovery pending external activation"); }
      else toast.success(`${r.count} competitors discovered`);
      const t = await sr.marketTwin(loc.id); setTwin({ loc, ...t });
    } catch (e) { toast.error(e?.response?.data?.detail || "Discovery failed"); } finally { setBusy(false); }
  };
  const autoSplit = async () => {
    try { const r = await sr.locationsAutoSplit(); toast.success(`Split ${money(r.plan_budget)} evenly — ${money(r.per_location)} per location`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't auto-split"); }
  };

  if (!locs) return <div className="sr-empty"><Loader2 className="sr-spin" /></div>;
  return (
    <div>
      <Head title="Locations" sub="Manage each location's budget, offer and destination — all under your one account." />
      <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={() => setAdding(!adding)} data-testid="add-location-btn"><Plus size={15} /> Add location</button>

      {adding && (
        <div className="sr-card" style={{ marginTop: 14 }}>
          <div style={{ display: "flex", gap: 10 }}>
            <input className="sr-input" placeholder="Search a location by name/address" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} data-testid="loc-search" />
            <button className="sr-btn sr-btn-primary" onClick={search} disabled={busy}>{busy ? <Loader2 size={16} className="sr-spin" /> : <Search size={16} />}</button>
          </div>
          {results.map((p) => (
            <button key={p.place_id} className="sr-card" style={{ marginTop: 8, textAlign: "left", cursor: "pointer", display: "flex", gap: 10, width: "100%" }} onClick={() => add(p)}>
              <MapPin size={16} color="var(--blue)" /><div><strong style={{ color: "var(--navy)" }}>{p.name}</strong><div className="sr-muted" style={{ fontSize: 13 }}>{p.address}</div></div>
            </button>
          ))}
        </div>
      )}

      {summary && locs.length > 0 && <BudgetRollup summary={summary} onAutoSplit={autoSplit} />}

      <div style={{ display: "grid", gap: 12, marginTop: 18 }}>
        {locs.length === 0 && <Honest icon={MapPin} title="No locations yet" body="Add your first business location to unlock per-location budgets, offers and trade-area targeting." />}
        {locs.map((l) => (
          <div key={l.id} className="sr-card" data-testid={`location-${l.id}`}>
            <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
              <div style={{ minWidth: 0 }}>
                <strong style={{ color: "var(--navy)" }}>{l.name}</strong>
                {l.verified ? <span className="sr-badge sr-badge-green" style={{ marginLeft: 8 }}><Check size={12} /> Verified</span>
                  : <span className="sr-badge sr-badge-amber" style={{ marginLeft: 8 }}>Unverified</span>}
                <div className="sr-muted" style={{ fontSize: 13, marginTop: 4 }}>{l.address}</div>
                <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 8, fontSize: 12.5, color: "var(--muted)" }}>
                  <span data-testid={`loc-budget-${l.id}`}><DollarSign size={12} style={{ verticalAlign: -1 }} /> {l.monthly_budget ? `${money(l.monthly_budget)}/mo` : "No budget set"}</span>
                  <span data-testid={`loc-offer-${l.id}`}><Tag size={12} style={{ verticalAlign: -1 }} /> {l.offer_snapshot?.headline || "Uses brand default offer"}</span>
                  <span data-testid={`loc-dest-${l.id}`}><Link2 size={12} style={{ verticalAlign: -1 }} /> {l.destination_url ? l.destination_url : "Uses brand default URL"}</span>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setEditing(editing === l.id ? null : l.id)} data-testid={`edit-loc-${l.id}`}>
                  <Sliders size={15} /> Overrides
                </button>
                <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => discover(l)} disabled={busy || !l.lat} data-testid={`discover-${l.id}`}>
                  <Target size={15} /> Competitors
                </button>
              </div>
            </div>
            {editing === l.id && <OverrideEditor loc={l} offers={offers} onDone={() => { setEditing(null); load(); }} />}
          </div>
        ))}
      </div>

      {twin && (
        <div className="sr-card" style={{ marginTop: 18 }} data-testid="market-twin">
          <div className="sr-eyebrow">Local Market Twin · {twin.loc.name}</div>
          <p className="sr-muted" style={{ marginTop: 8, fontSize: 14 }}>{twin.reasoning_summary}</p>
          <p style={{ fontSize: 12, marginTop: 6, color: "var(--muted)" }}><Info size={11} /> {twin.note}</p>
          <hr className="sr-divider" />
          <strong style={{ color: "var(--navy)" }}>Competitors ({(twin.competitors || []).length})</strong>
          <div style={{ display: "grid", gap: 8, marginTop: 10 }}>
            {(twin.competitors || []).map((c) => (
              <div key={c.id} style={{ display: "flex", justifyContent: "space-between", fontSize: 14, padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
                <span>{c.name} <span className="sr-muted">· {c.address}</span></span>
                <span className="sr-badge sr-badge-gray">{c.boundary_type === "polygon" ? "Custom polygon" : `Radius ${c.radius_m || 400}m`}</span>
              </div>
            ))}
            {!(twin.competitors || []).length && <p className="sr-muted" style={{ fontSize: 13 }}>No competitors discovered yet.</p>}
          </div>
        </div>
      )}
    </div>
  );
}

function BudgetRollup({ summary, onAutoSplit }) {
  const { plan_budget, allocated, remaining, over_budget, location_count } = summary;
  return (
    <div className="sr-card" style={{ marginTop: 16 }} data-testid="locations-budget-rollup">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <div>
          <div className="sr-eyebrow"><DollarSign size={13} style={{ verticalAlign: -2 }} /> Budget across {location_count} location{location_count === 1 ? "" : "s"}</div>
          <div style={{ display: "flex", gap: 22, marginTop: 8, flexWrap: "wrap" }}>
            <Metric label="Plan budget" value={money(plan_budget)} />
            <Metric label="Allocated" value={money(allocated)} />
            <Metric label={over_budget ? "Over by" : "Unallocated"} value={money(Math.abs(remaining))} tone={over_budget ? "red" : "green"} />
          </div>
        </div>
        <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={onAutoSplit} data-testid="budget-auto-split" disabled={!plan_budget}>
          <Sliders size={14} /> Auto-split evenly
        </button>
      </div>
      {plan_budget > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ height: 8, borderRadius: 999, background: "var(--border)", overflow: "hidden" }}>
            <div style={{ width: `${Math.min(100, (allocated / plan_budget) * 100)}%`, height: "100%", background: over_budget ? "var(--red,#DC2626)" : "var(--blue)" }} />
          </div>
          {over_budget && <p data-testid="over-budget-warning" style={{ fontSize: 12.5, color: "var(--red,#DC2626)", marginTop: 8 }}>
            <Info size={12} style={{ verticalAlign: -1 }} /> Your locations are allocated {money(allocated)}, which is more than the {money(plan_budget)} plan budget. That's allowed — just raise your plan budget or trim a location to stay balanced.
          </p>}
        </div>
      )}
      <p className="sr-muted" style={{ fontSize: 12, marginTop: 12 }}>
        <Shield size={12} style={{ verticalAlign: -1 }} /> Every location rolls up under your one account — adding locations never creates a separate bill or client login.
      </p>
    </div>
  );
}

function Metric({ label, value, tone }) {
  const color = tone === "red" ? "var(--red,#DC2626)" : tone === "green" ? "var(--green,#059669)" : "var(--navy)";
  return (
    <div>
      <div style={{ fontSize: 11, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color }}>{value}</div>
    </div>
  );
}

function OverrideEditor({ loc, offers, onDone }) {
  const [budget, setBudget] = useState(loc.monthly_budget || "");
  const [offerId, setOfferId] = useState(loc.offer_id || "");
  const [dest, setDest] = useState(loc.destination_url || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await sr.patchLocation(loc.id, {
        monthly_budget: budget === "" ? 0 : Number(budget),
        offer_id: offerId,
        destination_url: dest,
      });
      toast.success("Location overrides saved");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); setSaving(false); }
  };

  return (
    <div style={{ marginTop: 14, borderTop: "1px solid var(--border)", paddingTop: 14, display: "grid", gap: 12 }} data-testid={`override-editor-${loc.id}`}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <label style={{ fontSize: 12, fontWeight: 600 }}>Monthly budget
          <input className="sr-input" type="number" min="0" value={budget} onChange={(e) => setBudget(e.target.value)} placeholder="0" data-testid={`override-budget-${loc.id}`} style={{ marginTop: 4 }} />
        </label>
        <label style={{ fontSize: 12, fontWeight: 600 }}>Offer for this location <span className="sr-muted" style={{ fontWeight: 400 }}>(priority)</span>
          <select className="sr-select" value={offerId} onChange={(e) => setOfferId(e.target.value)} data-testid={`override-offer-${loc.id}`} style={{ marginTop: 4 }}>
            <option value="">Use brand default offer</option>
            {offers.map((o) => <option key={o.id} value={o.id}>{o.headline || o.name || o.code || o.id}</option>)}
          </select>
        </label>
      </div>
      <label style={{ fontSize: 12, fontWeight: 600 }}>Destination URL override
        <input className="sr-input" value={dest} onChange={(e) => setDest(e.target.value)} placeholder="https://…  (blank = brand default)" data-testid={`override-dest-${loc.id}`} style={{ marginTop: 4 }} />
      </label>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={onDone} disabled={saving}>Cancel</button>
        <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={save} disabled={saving} data-testid={`override-save-${loc.id}`}>
          {saving ? <Loader2 size={14} className="sr-spin" /> : <Check size={14} />} Save overrides
        </button>
      </div>
    </div>
  );
}
